
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { IChatService } from "./@chatscope/use-chat/src/interfaces/IChatService";
import { ChatEventType, MessageContentType, MessageDirection, AvatarPosition, ConnectionState, MessageStatus } from "./@chatscope/use-chat/src/enums";
import {
  ChatEventHandler,
  SendMessageServiceParams,
  SendTypingServiceParams,
  UpdateState,
} from "./@chatscope/use-chat/src/Types";
import { IStorage } from "./@chatscope/use-chat/src/interfaces";
import { ChatEvent, MessageEvent, UserTypingEvent, UserPresenceChangedEvent, ConnectionStateChangedEvent } from "./@chatscope/use-chat/src/events";
import { ChatMessage, ChatMessageParams } from "./@chatscope/use-chat/src/ChatMessage";
import { getToken } from "./getAccessToken";
import { UserId } from "./@chatscope/use-chat/src/Types";
import { Presence } from "./@chatscope/use-chat/src/Presence";
import { UserStatus, Participant, Conversation } from "./@chatscope/use-chat/src";
import { IPublicClientApplication } from "@azure/msal-browser";
import './App.css';
import { getUserData } from './utils/peopleSearch';




type EventHandlers = {
  onMessage: ChatEventHandler<
    ChatEventType.Message,
    ChatEvent<ChatEventType.Message>
  >;
  onConnectionStateChanged: ChatEventHandler<
    ChatEventType.ConnectionStateChanged,
    ChatEvent<ChatEventType.ConnectionStateChanged>
  >;
  onUserConnected: ChatEventHandler<
    ChatEventType.UserConnected,
    ChatEvent<ChatEventType.UserConnected>
  >;
  onUserDisconnected: ChatEventHandler<
    ChatEventType.UserDisconnected,
    ChatEvent<ChatEventType.UserDisconnected>
  >;
  onUserPresenceChanged: ChatEventHandler<
    ChatEventType.UserPresenceChanged,
    ChatEvent<ChatEventType.UserPresenceChanged>
  >;
  onUserTyping: ChatEventHandler<
    ChatEventType.UserTyping,
    ChatEvent<ChatEventType.UserTyping>
  >;
  [key: string]: any;
};
export class ChatService implements IChatService {
  private connection: HubConnection | null = null;
  storage?: IStorage;
  updateState: UpdateState;
  instance: IPublicClientApplication;
  private settingsJson: any;
  private presenceIntervalId?: NodeJS.Timeout;


  eventHandlers: EventHandlers = {
    onMessage: () => { },
    onConnectionStateChanged: () => { },
    onUserConnected: () => { },
    onUserDisconnected: () => { },
    onUserPresenceChanged: () => { },
    onUserTyping: () => { },
  };


  constructor(storage: IStorage, update: UpdateState, instance: IPublicClientApplication) {
    this.storage = storage;
    this.updateState = update;
    this.instance = instance;


    //new connection for Azure Signalr
    //TODO: ensure all functionality is the same as messageconnector
    this.connection = new HubConnectionBuilder()
      .withUrl(process.env.REACT_APP_HUB_URL as string, {
        accessTokenFactory: async () => {
          const token = await getToken(this.instance, null);
          return token;
        }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.startConnection()

    this.connection.on('ReceiveMessage', async (gmid, incomingMessage, upn) => {

      let msg = JSON.parse(incomingMessage).msg;
      let conv: Conversation = JSON.parse(incomingMessage).conv;
      let senderName = storage.getUser(gmid)?.[0]?.username;
      if (!senderName) {
        senderName = (await getUserData(instance, upn)).username;
      }

      const chatMessageParams: ChatMessageParams<MessageContentType.TextHtml> = {
        id: "",
        status: MessageStatus.DeliveredToDevice,
        contentType: MessageContentType.TextHtml,
        senderId: msg.senderId,
        senderName: senderName ?? "",
        direction: MessageDirection.Incoming,
        avatarPosition: AvatarPosition.Left,
        content: msg.content,
        createdTime: new Date()
        // updatedTime is optional, you can leave it out if you want
      };

      const message = new ChatMessage<MessageContentType.TextHtml>(chatMessageParams);

      const conversation = new Conversation({
        id: conv.id,
        participants: conv.participants,
      });

      if (this.eventHandlers.onMessage) {
        this.eventHandlers.onMessage(
          new MessageEvent({ message, conversation })
        );
      }

    });

    this.connection.on('ReceivePresences', async (users) => {
      for (let [key, value] of Object.entries(users)) {
        let userId: UserId = key;
        let currentStatus = UserStatus.Invisible;
        if (value === "Available") {
          currentStatus = UserStatus.Available;
        }
        let currentUser = this.storage?.getUser(userId);
        if (currentUser) {
          if (currentUser[0]?.presence.status !== currentStatus) {
            let presence = new Presence({ status: currentStatus });

            if (this.eventHandlers.onUserPresenceChanged) {
              this.eventHandlers.onUserPresenceChanged(
                new UserPresenceChangedEvent({
                  userId,
                  presence
                })
              );
            }
          }
        }
      }
    });

    this.connection.on('typing', (detail) => {
      const { userId, isTyping, conversationId, content, sender } = detail;
      if (this.eventHandlers.onUserTyping && sender !== this) {
        this.eventHandlers.onUserTyping(
          new UserTypingEvent({
            userId,
            isTyping,
            conversationId,
            content,
          })
        );
      }
    });


    this.connection.onreconnecting(() => {
      console.log('Connection is reconnecting...');
      if (this.eventHandlers.onConnectionStateChanged) {
        this.eventHandlers.onConnectionStateChanged(
          new ConnectionStateChangedEvent(ConnectionState.Connecting)
        );
      }
    });

    this.connection.onreconnected(() => {
      console.log('Connection is reconnected');
      if (this.eventHandlers.onConnectionStateChanged) {
        this.eventHandlers.onConnectionStateChanged(
          new ConnectionStateChangedEvent(ConnectionState.Connected)
        );
      }
    });

    this.connection.onclose(() => {
      console.log('Connection is closed');
      if (this.presenceIntervalId) {
        clearInterval(this.presenceIntervalId);
      }
      if (this.eventHandlers.onConnectionStateChanged) {
        this.eventHandlers.onConnectionStateChanged(
          new ConnectionStateChangedEvent(ConnectionState.Disconnected)
        );
      }
    });


  }

  startConnection() {
    console.log("Attempting to start connection to SignalR");
    if (this.connection) {
      this.connection.start()
        .then(() => {
          console.log('Connection started');
          if (this.eventHandlers.onConnectionStateChanged) {
            this.eventHandlers.onConnectionStateChanged(
              new ConnectionStateChangedEvent(ConnectionState.Initial)
            );
          }
          // Connection established, set up interval to run getPresence
          this.setPresenceInterval();
          // Listen for visibility changes
          document.addEventListener('visibilitychange', this.handleVisibilityChange);
        })
        .catch(err => console.log('Error while starting connection: ', err));
    }

  };
  handleVisibilityChange = () => {
    // Clear the current interval and start a new one
    if (this.presenceIntervalId) {
      clearInterval(this.presenceIntervalId);
    }
    this.setPresenceInterval();
  }

  setPresenceInterval() {
    const visibleInterval = parseInt(process.env.REACT_APP_PRESENCE_INTERVAL as string) || 10000;
    const hiddenInterval = parseInt(process.env.REACT_APP_PRESENCE_INTERVAL_HIDDEN as string) || 60000;
    const interval = document.visibilityState === 'visible' ? visibleInterval : hiddenInterval;
    // Call getPresence immediately
    this.getPresence();

    // Then set up the interval
    this.presenceIntervalId = setInterval(() => {
        this.getPresence();
    }, interval);
  }

  sendMessage({ message, conversationId }: SendMessageServiceParams) {
    if (!this.storage) {
      console.error('Storage is not defined');
      return null;
    }

    let conversationResult = this.storage.getConversation(conversationId);
    let conversation: Conversation<any> | undefined = Array.isArray(conversationResult) ? conversationResult[0] : conversationResult;

    if (!conversation) {
      console.error('Conversation is not defined');
      return null;
    }
    let participants: Participant[] = conversation.participants;
    if (!participants) {
      console.error('Participants are not defined');
      return null;
    }
    if (!conversation.participantExists(message.senderId)) {
      conversation.addParticipant(new Participant({ id: message.senderId, upn: message.senderId }));
    }
    let msg = JSON.stringify({ "msg": message, "conv": conversation });
    participants.forEach((participant) => {
      if (this.connection && message.senderId !== participant.id) {
        this.connection.invoke('SendMessageAsync', participant.id, msg, this?.storage?.getCurrentUser()?.upn)
          .catch(err => console.log('Error while sending message: ', err));
      }
    });
    return message;
  }

  sendTyping({ isTyping, content, conversationId, userId }: SendTypingServiceParams) {
    if (this.connection) {
      // this.connection.send('typing', { isTyping, content, conversationId, userId, sender: this })
      //  .catch(err => console.log('Error while sending typing notification: ', err));
    }
  }

  getPresence() {
    if (this.connection) {
      let allUsersArray: any[] = [];
      let allUsers = this.storage?.getUsers();
      if (allUsers) {
        allUsersArray = allUsers.map((user) => user.id);
      }
      // Only invoke GetPresencesAsync if allUsersArray is not empty
      if (allUsersArray.length > 0) {
        this.connection.invoke('GetPresencesAsync', allUsersArray)
          .catch(err => console.log('Error while getting presence: ', err));
      }
    }
  }

  async loadSettingsFile(): Promise<any> {
    if (this.connection) {
      const fileString = `${this?.storage?.getCurrentUser()?.id} settings.json`;
      try {
        if (!this.settingsJson) {
          const result = await this.connection.invoke('DownloadAsync', fileString);
          if (result.length === 0) {
            const settings = JSON.parse(process.env.REACT_APP_DEFAULT_SETTINGS as string);
            const defaultSettings = { favorites: [], settings: settings };
            await this.saveSettingsFile(fileString, btoa(JSON.stringify(defaultSettings)));
            this.settingsJson = defaultSettings;
            if (Notification.permission === "default" || Notification.permission === "denied") {
              Notification.requestPermission().then((permission) => {
                if (permission === "granted") {
                  this.settingsJson.settings.notifications = true;
                }
                else {
                  this.settingsJson.settings.notifications = false;
                }
              });
            }
          } else {
            this.settingsJson = JSON.parse(atob(result));
          }
        }
        if (this.settingsJson.settings.notifications === true && (Notification.permission === "default" || Notification.permission === "denied")) {
          await Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              this.settingsJson.settings.notifications = true;
            }
            else {
              this.settingsJson.settings.notifications = false;
            }
          });
        }
        return this.settingsJson;
      } catch (error) {
        console.log('[loadSettingsFile] Error while invoking DownloadAsync:', error);
        return [];
      }
    }
    return [];
  }

  async saveSettingsFile(fileName: string, fileBytes: any) {
    if (this.connection) {
      this.connection.invoke('UploadAsync', fileName, fileBytes)
        .then(() => {
          console.log("[saveSettingsFile] File uploaded successfully");
        })
        .catch((error) => {
          console.log('[saveSettingsFile] Error while invoking UploadAsync:', error);
        });
    }
  }

  // The ChatProvider registers callbacks with the service.
  // These callbacks are necessary to notify the provider of the changes.
  // For example, when your service receives a message, you need to run an onMessage callback,
  // because the provider must know that the new message arrived.
  // Here you need to implement callback registration in your service.
  // You can do it in any way you like. It's important that you will have access to it elsewhere in the service.
  on<T extends ChatEventType, H extends ChatEvent<T>>(
    evtType: T,
    evtHandler: ChatEventHandler<T, H>
  ) {
    const key = `on${evtType.charAt(0).toUpperCase()}${evtType.substring(1)}`;

    if (key in this.eventHandlers) {
      this.eventHandlers[key] = evtHandler;
    }
  }

  // The ChatProvider can unregister the callback.
  // In this case remove it from your service to keep it clean.
  off<T extends ChatEventType, H extends ChatEvent<T>>(
    evtType: T,
    eventHandler: ChatEventHandler<T, H>
  ) {
    const key = `on${evtType.charAt(0).toUpperCase()}${evtType.substring(1)}`;
    if (key in this.eventHandlers) {
      this.eventHandlers[key] = () => { };
    }
  }
}